/**
 * DiscoverModal — resolve a source from a platform plus a URL or handle (`intel:discover`, queued for the content
 * worker). Demo page: `/connected-apps` (connect flow). Needs CanManageAgents (the caller gates it).
 */
import React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckCircle, Search } from 'lucide-react';
import { z } from 'zod';
import type { DiscoverIntelSourceResponse } from '../../../generated/api/types.gen.js';
import { zIntelSourceDiscoverRequest } from '../../../generated/api/zod.gen.js';
import {
  discoverIntelSourceMutation,
  listIntelPlatformsOptions,
} from '../../../generated/api/@tanstack/react-query.gen.js';
import { Modal, Button, Callout, Skeleton } from '../../../components/ui';
import { Form, SelectFormField, SubmitButton, TextFormField, useZodForm } from '../../../components/form';

interface DiscoverModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

// The platform comes from the generated request; the single "URL or handle" box is UI-only and is split on submit.
const discoverSchema = zIntelSourceDiscoverRequest.pick({ platform: true }).extend({ input: z.string().trim().min(1) });

interface DiscoverFormProps {
  platforms: { value: string; label: string; description?: string }[];
  onCreated: () => void;
  onClose: () => void;
}

const DiscoverForm: React.FC<DiscoverFormProps> = ({ platforms, onCreated, onClose }) => {
  const form = useZodForm(discoverSchema, { defaultValues: { platform: platforms[0]?.value ?? '', input: '' } });
  const mutation = useMutation(discoverIntelSourceMutation());
  const [result, setResult] = React.useState<DiscoverIntelSourceResponse | null>(null);
  const platform = form.watch('platform');
  const description = platforms.find((p) => p.value === platform)?.description;

  if (result?.queued) {
    return (
      <div className="space-y-4">
        <p className="flex items-center gap-2 text-sm text-success">
          <CheckCircle size={16} aria-hidden="true" /> Discovery queued
        </p>
        <p className="rounded-lg bg-surface-alt p-4 text-sm text-muted">
          The content worker resolves this source in the background. It appears in the list when it is found.
        </p>
        <Button size="sm" onClick={onClose} className="w-full">
          Done
        </Button>
      </div>
    );
  }

  return (
    <Form
      form={form}
      className="gap-4"
      onSubmit={async (values) => {
        const isUrl = values.input.startsWith('http');
        const res = await mutation.mutateAsync({
          body: { platform: values.platform, ...(isUrl ? { url: values.input } : { handle: values.input }) },
        });
        if (res.queued) {
          setResult(res);
          onCreated();
        } else if (!res.found) {
          throw new Error(res.error ?? 'Source not found on this platform.');
        }
      }}
    >
      <SelectFormField name="platform" label="Platform" options={platforms} description={description} required />
      <TextFormField name="input" label="URL or handle" placeholder="https://... or @username" required />
      <SubmitButton variant="primary" size="md">
        <Search size={16} aria-hidden="true" /> Discover
      </SubmitButton>
    </Form>
  );
};

export const DiscoverModal: React.FC<DiscoverModalProps> = ({ open, onClose, onCreated }) => {
  const { data, isLoading, isError, refetch } = useQuery({ ...listIntelPlatformsOptions(), enabled: open });
  const platforms = (data?.data ?? [])
    .filter((p) => p.enabled)
    .map((p) => ({ value: p.slug, label: p.name, description: p.description ?? undefined }));

  return (
    <Modal open={open} onClose={onClose} title="Discover a source" maxWidth="lg">
      {isLoading ? (
        <div className="space-y-3">
          <Skeleton height={40} />
          <Skeleton height={40} />
        </div>
      ) : isError ? (
        <Callout
          tone="danger"
          title="Could not load the platforms"
          actions={
            <Button size="sm" variant="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          Check your connection and try again.
        </Callout>
      ) : platforms.length === 0 ? (
        <Callout tone="accent" title="No platforms enabled">
          Enable a platform in the integrations settings before discovering sources.
        </Callout>
      ) : (
        // Remounts per opening so the form starts empty.
        <DiscoverForm key={String(open)} platforms={platforms} onCreated={onCreated} onClose={onClose} />
      )}
    </Modal>
  );
};
